/**
 * 旧 業務の画面の URL（`/businesses/$businessId/v2` ・ `/logic` ・ `/dataflow`）を業務詳細へ倒す。
 *
 * 業務ルールの見方はデータの流れの図 1 つに寄せ、図を業務詳細の URL（`/businesses/$businessId`）へ移した
 * （Dhik 2026-09-30）。旧 URL のブックマークと、画面の外に貼られたリンクを 404 にしないために残す。
 * **案件（`?pj=`）は持ったまま送る** — 落とすと、案件を選んで開いた利用者が案件の外れたマスターへ着き、
 * 外れたことは画面に出ない。開いていた業務ルール（`?op=`）と左右比較の開閉は引き継がない（図は
 * 業務ルールを URL で開かない）。
 *
 * 描画側で行き先を宣言する（`LegacyUserStoryRedirect` と同じ形）。ルートに検索条件の検証器を張らない
 * のも同じ理由で、張ると倒すだけの URL が案件モードの画面として数えられる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 旧 業務詳細 v2 ・ 業務ロジック ・ データの流れの URL を、業務の番号と案件を引き継いだまま業務詳細へ倒す。
 * @graph-connects usePjSearchNumber [calls] URL の案件番号を読む
 */
import { Navigate, useParams } from '@tanstack/react-router';

import { pjLinkSearch } from './pj-link.js';
import { usePjSearchNumber } from './receivers/pj-number.js';
import { BUSINESS_DETAIL_ROUTE } from './route-paths.js';

/**
 * 旧 業務の画面の URL を、業務の番号と案件を引き継いで業務詳細へ倒す。
 *
 * @returns 業務詳細への倒し
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 旧 業務の画面の URL を、業務の番号と案件を引き継いだまま業務詳細へ倒す。
 * @graph-connects pjLinkSearch [calls] 案件を検索条件へ載せる
 */
export function LegacyBusinessRedirect() {
  const { businessId } = useParams({ strict: false });
  const pj = usePjSearchNumber();
  return (
    <Navigate
      to={BUSINESS_DETAIL_ROUTE}
      params={{ businessId: businessId ?? '' }}
      {...pjLinkSearch(pj === null ? undefined : String(pj))}
      replace
    />
  );
}
